class RsStories extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.dialog = this.querySelector('dialog');
    this.slides = [...this.querySelectorAll('[data-story-slide]')];
    this.segments = [...this.querySelectorAll('[data-story-segment]')];
    this.stage = this.querySelector('[data-story-stage]');
    this.pauseButton = this.querySelector('[data-story-pause]');
    this.muteButton = this.querySelector('[data-story-mute]');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.muted = true;
    this.index = 0;
    this.runToken = 0;
    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-story-open]');
      if (opener) this.open(opener.dataset.storyOpen, opener);
      if (event.target.closest('[data-story-close]')) this.dialog.close();
      if (event.target.closest('[data-story-prev]')) this.select(this.index - 1);
      if (event.target.closest('[data-story-next]')) this.select(this.index + 1);
      if (event.target.closest('[data-story-pause]')) { this.pausedByUser = !this.pausedByUser; this.syncPlayback(); }
      if (event.target.closest('[data-story-mute]')) {
        this.muted = !this.muted;
        if (this.video) this.video.muted = this.muted;
        this.renderTools();
      }
      if (event.target === this.dialog) this.dialog.close();
    }, { signal });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const direction = document.documentElement.dir === 'rtl' ? -1 : 1;
        this.select(this.index + (event.key === 'ArrowRight' ? direction : -direction));
      }
      // Space on buttons retains its native activation behavior.
      if (event.code === 'Space' && !event.target.closest('button,a,input,select,textarea')) {
        event.preventDefault(); this.pausedByUser = !this.pausedByUser; this.syncPlayback();
      }
    }, { signal });
    this.dialog.addEventListener('close', () => {
      this.stop();
      this.opener?.focus({ preventScroll: true });
    }, { signal });
    // Press-and-hold pauses a story; a horizontal swipe moves between stories.
    this.stage.addEventListener('pointerdown', (event) => {
      if (event.target.closest('a,button') || event.button !== 0) return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      this.holding = true;
      this.stage.setPointerCapture(event.pointerId);
      this.syncPlayback();
    }, { signal });
    const release = (event) => {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      const deltaX = event.clientX - this.pointer.x;
      const deltaY = event.clientY - this.pointer.y;
      this.pointer = null;
      this.holding = false;
      if (event.type !== 'pointercancel' && Math.abs(deltaX) > 55 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
        const direction = document.documentElement.dir === 'rtl' ? -1 : 1;
        this.select(this.index + (deltaX < 0 ? direction : -direction));
      }
      this.syncPlayback();
    };
    this.stage.addEventListener('pointerup', release, { signal });
    this.stage.addEventListener('pointercancel', release, { signal });
    document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });
    this.reducedMotion.addEventListener('change', () => {
      if (this.reducedMotion.matches) this.pausedByUser = true;
      this.syncPlayback();
    }, { signal });
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.contains(event.target)) return;
      const opener = event.target.querySelector('[data-story-open]');
      if (opener) this.open(opener.dataset.storyOpen, opener, true);
    }, { signal });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target.contains(this)) this.dialog.close();
    }, { signal });
    for (const slide of this.slides) {
      const video = slide.querySelector('video');
      if (!video) continue;
      video.addEventListener('ended', () => {
        if (video === this.video && this.dialog.open && !this.pausedByUser && !document.hidden) this.advance();
      }, { signal });
      video.addEventListener('playing', () => video.classList.add('is-playing'), { signal });
      video.addEventListener('error', () => {
        video.dataset.failed = 'true';
        video.classList.remove('is-playing');
        if (video === this.video) { this.video = null; this.elapsed = 0; this.syncPlayback(); }
      }, { signal });
    }
  }
  open(id, opener, editor = false) {
    const index = this.slides.findIndex((slide) => slide.dataset.storySlide === id);
    if (index < 0) return;
    this.opener = opener;
    this.pausedByUser = editor || this.reducedMotion.matches;
    if (!this.dialog.open) this.dialog.showModal();
    this.select(index);
  }
  select(index) {
    if (index < 0 || index >= this.slides.length) return;
    this.stop();
    this.index = index;
    this.elapsed = 0;
    this.holding = false;
    this.slides.forEach((slide, i) => {
      slide.hidden = i !== index;
      const video = slide.querySelector('video');
      if (video) { video.pause(); try { video.currentTime = 0; } catch { /* Metadata may not be loaded yet. */ } }
    });
    this.current = this.slides[index];
    const candidate = this.current.querySelector('video');
    this.video = candidate?.dataset.failed ? null : candidate;
    if (this.video) this.video.muted = this.muted;
    this.querySelector('[data-story-title]').textContent = this.current.dataset.storyLabel;
    this.querySelector('[data-story-announcement]').textContent = `${this.current.dataset.storyLabel}. Story ${index + 1} of ${this.slides.length}.`;
    this.querySelector('[data-story-prev]').disabled = index === 0;
    this.querySelector('[data-story-next]').disabled = index === this.slides.length - 1;
    this.renderProgress(0);
    this.syncPlayback();
  }
  stop() {
    cancelAnimationFrame(this.frame);
    this.runToken += 1;
    this.video?.pause();
    this.lastFrame = null;
  }
  syncPlayback() {
    this.stop();
    if (!this.dialog.open || !this.current) return;
    this.renderTools();
    if (this.pausedByUser || this.holding || document.hidden) return;
    const token = this.runToken;
    if (this.video) {
      const startingVideo = this.video;
      startingVideo.play().then(() => {
        if (token !== this.runToken || !this.dialog.open || this.pausedByUser || document.hidden) {
          if (startingVideo !== this.video || this.pausedByUser || this.holding || document.hidden || !this.dialog.open) startingVideo.pause();
        }
      }).catch(() => {
        if (token !== this.runToken) return;
        this.pausedByUser = true;
        this.renderTools();
      });
    }
    const tick = (now) => {
      if (token !== this.runToken || !this.dialog.open || this.pausedByUser || this.holding || document.hidden) return;
      if (this.lastFrame != null) this.elapsed += Math.min(now - this.lastFrame, 200);
      this.lastFrame = now;
      let progress;
      if (this.video && Number.isFinite(this.video.duration) && this.video.duration > 0) progress = this.video.currentTime / this.video.duration;
      else if (this.video) progress = 0;
      else progress = this.elapsed / (Math.max(3, Number(this.current.dataset.duration) || 7) * 1000);
      this.renderProgress(progress);
      if (progress >= 1 && !this.video) { this.advance(); return; }
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }
  advance() {
    if (this.index < this.slides.length - 1) this.select(this.index + 1);
    else this.dialog.close();
  }
  renderProgress(progress) {
    this.segments.forEach((segment, index) => {
      segment.firstElementChild.style.transform = `scaleX(${index < this.index ? 1 : index > this.index ? 0 : Math.max(0, Math.min(1, progress))})`;
    });
  }
  renderTools() {
    const paused = this.pausedByUser || this.holding || document.hidden;
    this.pauseButton.setAttribute('aria-label', paused ? 'Play story' : 'Pause story');
    this.pauseButton.firstElementChild.textContent = paused ? '▶' : 'Ⅱ';
    this.muteButton.hidden = !this.video;
    this.muteButton.setAttribute('aria-label', this.muted ? 'Unmute video' : 'Mute video');
    this.muteButton.setAttribute('aria-pressed', String(this.muted));
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.stop();
    this.dialog?.close();
    this.slides?.forEach((slide) => slide.querySelector('video')?.pause());
  }
}
if (!customElements.get('rs-stories')) customElements.define('rs-stories', RsStories);
